import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Brain,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Globe,
  Loader2,
  RefreshCw,
  Server,
  AlertTriangle,
} from "lucide-react";
import { apiv2, type GlobalLLMConfigOut, type LLMProviderInfo } from "../apiv2";
import { useAuth } from "../auth";

interface Props {
  onBack: () => void;
}

export default function LLMSettings({ onBack }: Props) {
  const { isAdmin } = useAuth();
  const [providers, setProviders] = useState<LLMProviderInfo[]>([]);
  const [config, setConfig] = useState<GlobalLLMConfigOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    online: boolean;
    error?: string | null;
  } | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<{
    ok: boolean;
    gateway_url: string;
    status_code: number | null;
    latency_ms: number;
    model_count: number;
    error: string | null;
  } | null>(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Form state
  const [provider, setProvider] = useState("anthropic");
  const [modelName, setModelName] = useState("claude-sonnet-4-20250514");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4096);

  // Provider-specific additional config
  const [awsRegion, setAwsRegion] = useState("us-east-1");
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [readTimeout, setReadTimeout] = useState(300);
  const [azureEndpoint, setAzureEndpoint] = useState("");
  const [azureDeployment, setAzureDeployment] = useState("");
  const [azureApiVersion, setAzureApiVersion] = useState("2024-12-01-preview");

  // Epoch (Deloitte LiteLLM gateway)
  const [epochGatewayUrl, setEpochGatewayUrl] = useState(
    "https://proxy.epoch.deloitte.com",
  );
  const [epochGatewayTimeout, setEpochGatewayTimeout] = useState(60);
  const [epochModels, setEpochModels] = useState<
    { id: string; label: string }[]
  >([]);
  const [epochModelSource, setEpochModelSource] = useState<
    "live" | "fallback" | null
  >(null);
  const [epochModelError, setEpochModelError] = useState("");
  const [epochLoadingModels, setEpochLoadingModels] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [provs, cfg] = await Promise.all([
        apiv2.listProviders(),
        apiv2.getGlobalLLMConfig(),
      ]);
      setProviders(provs);
      setConfig(cfg);
      if (cfg) {
        setProvider(cfg.provider);
        setModelName(cfg.model_name);
        setTemperature(cfg.temperature);
        setMaxTokens(cfg.max_tokens);
        // Parse additional_config
        try {
          const extra = JSON.parse(cfg.additional_config || "{}");
          setAwsRegion(extra.aws_region || "us-east-1");
          setAwsAccessKeyId(extra.aws_access_key_id || "");
          setAwsSecretAccessKey(extra.aws_secret_access_key || "");
          setReadTimeout(
            extra.read_timeout ? parseInt(extra.read_timeout) : 300,
          );
          setAzureEndpoint(extra.azure_endpoint || "");
          setAzureDeployment(extra.azure_deployment || "");
          setAzureApiVersion(extra.azure_api_version || "2024-12-01-preview");
          setEpochGatewayUrl(
            extra.gateway_url || "https://proxy.epoch.deloitte.com",
          );
          setEpochGatewayTimeout(
            extra.gateway_timeout ? parseInt(extra.gateway_timeout) : 60,
          );
        } catch {
          /* ignore */
        }
      }
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const currentProvider = providers.find((p) => p.id === provider);
  // For Epoch, prefer the live-fetched catalog (filtered + deduped by
  // the backend) over the seeded fallback list from /llm/providers.
  // For every other provider the registry list is authoritative.
  const modelOptions =
    provider === "epoch" && epochModels.length > 0
      ? epochModels
      : currentProvider?.models || [];

  async function loadEpochModels(refresh = false) {
    setEpochLoadingModels(true);
    setEpochModelError("");
    try {
      const result = await apiv2.lookupEpochModels({
        gateway_url: epochGatewayUrl,
        api_key: apiKey,
        refresh,
      });
      setEpochModels(result.models);
      setEpochModelSource(result.source);
      if (result.error) setEpochModelError(result.error);
      // If the current model is no longer in the live catalog, drop
      // to the first available — better than a silent invalid submit.
      if (
        result.models.length > 0 &&
        !result.models.some((m) => m.id === modelName)
      ) {
        setModelName(result.models[0].id);
      }
    } catch (e: unknown) {
      setEpochModelError((e as Error).message);
    } finally {
      setEpochLoadingModels(false);
    }
  }

  // When provider changes, pick first model as default
  function handleProviderChange(newProvider: string) {
    setProvider(newProvider);
    const prov = providers.find((p) => p.id === newProvider);
    if (prov && prov.models.length > 0) {
      setModelName(prov.models[0].id);
    }
    setApiKey("");
    setTestResult(null);
  }

  function buildAdditionalConfig(): Record<string, string> {
    const extra: Record<string, string> = {};
    if (provider === "aws_bedrock") {
      extra.aws_region = awsRegion;
      if (awsAccessKeyId) extra.aws_access_key_id = awsAccessKeyId;
      if (awsSecretAccessKey) extra.aws_secret_access_key = awsSecretAccessKey;
      extra.read_timeout = String(readTimeout);
    }
    if (provider === "azure_openai") {
      if (azureEndpoint) extra.azure_endpoint = azureEndpoint;
      if (azureDeployment) extra.azure_deployment = azureDeployment;
      extra.azure_api_version = azureApiVersion;
    }
    if (provider === "epoch") {
      extra.gateway_url = epochGatewayUrl;
      if (epochGatewayTimeout) {
        extra.gateway_timeout = String(epochGatewayTimeout);
      }
    }
    return extra;
  }

  async function handleSave() {
    setSaving(true);
    setSuccess("");
    setError("");
    setTestResult(null);
    try {
      const result = await apiv2.setGlobalLLMConfig({
        provider,
        model_name: modelName,
        api_key: apiKey || undefined,
        temperature,
        max_tokens: maxTokens,
        additional_config: buildAdditionalConfig(),
      });
      setConfig(result);
      setApiKey(""); // Clear plaintext after save
      setSuccess("Global LLM configuration saved successfully");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const result = await apiv2.getLLMStatus();
      setTestResult({ online: result.online, error: result.error });
    } catch (e: unknown) {
      setTestResult({ online: false, error: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function handleProbe() {
    // Lightweight reachability check — GET /v1/models, no inference
    // cost. Useful for diagnosing DNS / auth / outage state without
    // burning the cap budget.
    setProbing(true);
    setProbeResult(null);
    setError("");
    try {
      // If the admin has entered new gateway_url + api_key in the form,
      // probe THOSE before saving. Otherwise probe the saved config.
      const body: { gateway_url?: string; api_key?: string } = {};
      if (provider === "epoch" && epochGatewayUrl)
        body.gateway_url = epochGatewayUrl;
      if (apiKey) body.api_key = apiKey;
      const result = await apiv2.probeEpoch(body);
      setProbeResult(result);
    } catch (e: unknown) {
      setProbeResult({
        ok: false,
        gateway_url: epochGatewayUrl,
        status_code: null,
        latency_ms: 0,
        model_count: 0,
        error: (e as Error).message,
      });
    } finally {
      setProbing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-brand-500" size={32} />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/30 hover:text-brand-500 text-xs mb-6 transition"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
            <Brain size={20} className="text-brand-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold">LLM Configuration</h1>
            <p className="text-xs text-white/30">
              System-wide AI model settings
            </p>
          </div>
        </div>

        {/* Status indicator */}
        {config && (
          <div
            className={`mb-6 p-3 rounded-xl border text-sm flex items-center gap-2 ${
              config.has_api_key
                ? "bg-green-500/10 border-green-500/20 text-green-400"
                : "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
            }`}
          >
            {config.has_api_key ? (
              <>
                <Check size={16} />
                Configured — {currentProvider?.label || config.provider} /{" "}
                {config.model_name}
              </>
            ) : (
              <>
                <AlertTriangle size={16} />
                No API key set — agents cannot run until configured
              </>
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-xl text-sm text-green-400 flex items-center gap-2">
            <Check size={16} /> {success}
          </div>
        )}

        {/* Provider Selection */}
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 mb-6">
          <h2 className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-4">
            <Globe size={16} className="text-brand-500" /> Provider
          </h2>

          <div className="grid grid-cols-2 gap-3">
            {providers.map((p) => (
              <button
                key={p.id}
                onClick={() => handleProviderChange(p.id)}
                disabled={!isAdmin}
                className={`p-4 rounded-xl border text-left transition ${
                  provider === p.id
                    ? "border-brand-500/40 bg-brand-500/10"
                    : "border-white/5 bg-white/[0.02] hover:border-white/10"
                } ${!isAdmin ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="text-sm font-medium">{p.label}</div>
                <div className="text-[11px] text-white/30 mt-1">
                  {p.models.length} model{p.models.length !== 1 ? "s" : ""}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Model + Credentials */}
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 mb-6">
          <h2 className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-4">
            <Server size={16} className="text-brand-500" /> Model & Credentials
          </h2>

          <div className="space-y-4">
            {/* Model */}
            <div>
              <label className="block text-xs text-white/40 mb-1">Model</label>
              <div className="relative">
                <select
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  disabled={!isAdmin}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white appearance-none focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                >
                  {modelOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 top-3 text-white/30 pointer-events-none"
                />
              </div>
            </div>

            {/* API Key */}
            {(provider === "anthropic" ||
              provider === "openai" ||
              provider === "azure_openai" ||
              provider === "epoch") && (
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  API Key{" "}
                  {config?.has_api_key && !apiKey && (
                    <span className="text-green-400/60">
                      (saved — enter new to replace)
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    disabled={!isAdmin}
                    placeholder={
                      config?.has_api_key ? "••••••••••••••••" : "Enter API key"
                    }
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50 pr-10"
                  />
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-2.5 text-white/30 hover:text-white/60"
                  >
                    {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* AWS Bedrock fields */}
            {provider === "aws_bedrock" && (
              <>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    AWS Region
                  </label>
                  <input
                    type="text"
                    value={awsRegion}
                    onChange={(e) => setAwsRegion(e.target.value)}
                    disabled={!isAdmin}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    AWS Access Key ID{" "}
                    <span className="text-white/20">
                      (optional — uses instance role if empty)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={awsAccessKeyId}
                    onChange={(e) => setAwsAccessKeyId(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="AKIA..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    AWS Secret Access Key
                  </label>
                  <div className="relative">
                    <input
                      type={showKey ? "text" : "password"}
                      value={awsSecretAccessKey}
                      onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                      disabled={!isAdmin}
                      placeholder="Enter secret key"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50 pr-10"
                    />
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-3 top-2.5 text-white/30 hover:text-white/60"
                    >
                      {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    Read Timeout{" "}
                    <span className="text-white/20">
                      (seconds — increase for large/slow models like Opus)
                    </span>
                  </label>
                  <input
                    type="number"
                    min={30}
                    max={900}
                    value={readTimeout}
                    onChange={(e) =>
                      setReadTimeout(
                        Math.max(30, parseInt(e.target.value) || 300),
                      )
                    }
                    disabled={!isAdmin}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                  <p className="text-[11px] text-white/20 mt-1">
                    Default: 300s (5 min). Opus models may need 300-600s.
                  </p>
                </div>
              </>
            )}

            {/* Azure OpenAI fields */}
            {provider === "azure_openai" && (
              <>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    Azure Endpoint
                  </label>
                  <input
                    type="text"
                    value={azureEndpoint}
                    onChange={(e) => setAzureEndpoint(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="https://your-resource.openai.azure.com/"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    Deployment Name
                  </label>
                  <input
                    type="text"
                    value={azureDeployment}
                    onChange={(e) => setAzureDeployment(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="gpt-4o"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    API Version
                  </label>
                  <input
                    type="text"
                    value={azureApiVersion}
                    onChange={(e) => setAzureApiVersion(e.target.value)}
                    disabled={!isAdmin}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
              </>
            )}

            {/* Deloitte Epoch fields */}
            {provider === "epoch" && (
              <>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    Gateway URL{" "}
                    <span className="text-white/20">
                      (e.g. https://proxy.epoch.deloitte.com)
                    </span>
                  </label>
                  <input
                    data-testid="epoch-gateway-url"
                    type="text"
                    value={epochGatewayUrl}
                    onChange={(e) => setEpochGatewayUrl(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="https://proxy.epoch.deloitte.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    Request Timeout{" "}
                    <span className="text-white/20">(seconds, optional)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={600}
                    value={epochGatewayTimeout}
                    onChange={(e) =>
                      setEpochGatewayTimeout(
                        Math.max(0, parseInt(e.target.value) || 0),
                      )
                    }
                    disabled={!isAdmin}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <button
                    data-testid="epoch-fetch-models"
                    onClick={() => loadEpochModels(true)}
                    disabled={
                      !isAdmin ||
                      epochLoadingModels ||
                      !epochGatewayUrl ||
                      !apiKey
                    }
                    className="px-4 py-2 bg-white/5 border border-white/10 text-white/60 rounded-xl text-xs hover:border-brand-500/30 hover:text-white transition disabled:opacity-30 flex items-center gap-2"
                  >
                    {epochLoadingModels ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Fetch model catalog
                  </button>
                  {!apiKey && !config?.has_api_key && (
                    <p className="text-[11px] text-white/30 mt-1">
                      Enter the API key above first.
                    </p>
                  )}
                  {epochModelSource === "live" && (
                    <p className="text-[11px] text-green-400/70 mt-1">
                      Loaded {epochModels.length} models from gateway.
                    </p>
                  )}
                  {epochModelSource === "fallback" && (
                    <p className="text-[11px] text-yellow-400/70 mt-1">
                      Gateway unreachable — showing fallback catalog.{" "}
                      {epochModelError ? `(${epochModelError})` : ""}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Temperature & Max Tokens */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Temperature ({temperature})
                </label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  disabled={!isAdmin}
                  className="w-full accent-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Max Tokens
                </label>
                <input
                  type="number"
                  value={maxTokens}
                  onChange={(e) =>
                    setMaxTokens(parseInt(e.target.value) || 4096)
                  }
                  disabled={!isAdmin}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {isAdmin && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-brand-500 to-green-500 text-black font-semibold rounded-xl text-sm hover:brightness-110 transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Check size={16} />
              )}
              Save Configuration
            </button>

            <button
              onClick={handleTest}
              disabled={testing || !config?.has_api_key}
              className="px-5 py-2.5 bg-white/5 border border-white/10 text-white/60 rounded-xl text-sm hover:border-brand-500/30 hover:text-white transition disabled:opacity-30 flex items-center gap-2"
              title="Sends a tiny chat completion (~$0.0002). Verifies end-to-end inference works."
            >
              {testing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              Test Connection
            </button>

            {provider === "epoch" && (
              <button
                data-testid="epoch-probe-button"
                onClick={handleProbe}
                disabled={probing}
                className="px-5 py-2.5 bg-white/5 border border-white/10 text-white/60 rounded-xl text-sm hover:border-brand-500/30 hover:text-white transition disabled:opacity-30 flex items-center gap-2"
                title="Hits GET /v1/models — no inference cost. Use to diagnose DNS / auth / outage without burning the cap."
              >
                {probing ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Globe size={16} />
                )}
                Probe Connectivity
              </button>
            )}
          </div>
        )}

        {/* Test result */}
        {testResult && (
          <div
            className={`mt-4 p-3 rounded-xl border text-sm flex items-center gap-2 ${
              testResult.online
                ? "bg-green-500/10 border-green-500/20 text-green-400"
                : "bg-red-500/10 border-red-500/20 text-red-400"
            }`}
          >
            {testResult.online ? (
              <>
                <Check size={16} /> LLM connection successful
              </>
            ) : (
              <>
                <AlertTriangle size={16} /> Connection failed
                {testResult.error ? `: ${testResult.error}` : ""}
              </>
            )}
          </div>
        )}

        {/* Probe result */}
        {probeResult && (
          <div
            data-testid="epoch-probe-result"
            className={`mt-4 p-3 rounded-xl border text-sm flex items-start gap-2 ${
              probeResult.ok
                ? "bg-green-500/10 border-green-500/20 text-green-400"
                : "bg-red-500/10 border-red-500/20 text-red-400"
            }`}
          >
            {probeResult.ok ? (
              <Check size={16} className="mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              {probeResult.ok ? (
                <>
                  <div>
                    Gateway reachable — {probeResult.model_count} models in{" "}
                    {probeResult.latency_ms.toFixed(0)}ms
                  </div>
                  {probeResult.gateway_url && (
                    <div className="text-[11px] text-white/30 mt-1 font-mono">
                      {probeResult.gateway_url}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    Probe failed
                    {probeResult.status_code
                      ? ` (HTTP ${probeResult.status_code})`
                      : ""}
                    {probeResult.error ? `: ${probeResult.error}` : ""}
                  </div>
                  {probeResult.gateway_url && (
                    <div className="text-[11px] text-white/30 mt-1 font-mono">
                      {probeResult.gateway_url}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
